import type { Prospect } from '../rede/contract'
import { decisionOf } from '../rede/engine'
import { suggestionOf, type AmbassadorSuggestion, type SuggestionNetwork } from '../rede/kinds/ambassador'
import { fmtAudience } from '../rede/logic'
import type { Tone } from '../shared/ui'
import type { Ambassador, AmbassadorNetwork, AmbassadorStatus } from './contract'

/* Staff › Rede › Embaixadores — regras de tela (puras, testadas): situação, alcance, filtros, KPIs, motor de busca,
   fila do cliente, impacto e o recorte da foto. */

export const STATUS_META: Record<AmbassadorStatus, { label: string; tone: Tone }> = {
    ativo: { label: 'Ativo', tone: 'green' },
    pausado: { label: 'Pausado', tone: 'neutral' },
    termo: { label: 'Aguardando termo', tone: 'err' },
    novo: { label: 'Aguardando 1ª peça', tone: 'info' },
    confirmar: { label: 'A confirmar', tone: 'warn' },
}

export const num = (n: number) => n.toLocaleString('pt-BR')

/** Cor estável do avatar a partir do id. */
export const colorIndex = (id: string) => [...id].reduce((t, c) => t + c.charCodeAt(0), 0)

export const firstName = (name: string) => name.split(' ')[0] ?? name

// --- Embaixadores atuais ---

export const reachOf = (a: Pick<Ambassador, 'networks'>) => a.networks.reduce((t, r) => t + r.n, 0)

/** Rede com mais seguidores (a primeira, se nenhuma tem número). */
export function topNetwork<T extends { n: number }>(networks: T[]): T | null {
    return networks.reduce<T | null>((best, r) => (!best || r.n > best.n ? r : best), null)
}

export const unitOf = (network: string) => (network === 'YouTube' ? 'inscritos' : network === 'Facebook' ? 'curtidas' : network === 'LinkedIn' ? 'conexões' : 'seguidores')

/** "1 mi seguidores", "500+ conexões"; '' quando não há número. */
export function audienceLabel(r: Pick<AmbassadorNetwork, 'r' | 'n'>): string {
    if (!r.n) return ''
    const value = r.r === 'LinkedIn' && r.n >= 500 ? '500+' : fmtAudience(r.n)
    return `${value} ${unitOf(r.r)}`
}

/** "Embaixadora ChildFund Brasil" -> "Embaixadora oficial" (coluna Vínculo). */
export const shortBond = (v: string) => v.replace('Embaixadora ChildFund Brasil', 'Embaixadora oficial').replace('Embaixador ChildFund Brasil', 'Embaixador oficial')

export function categoryOptions(list: Ambassador[]): { id: string; label: string; count: number }[] {
    const cats = [...new Set(list.map(a => a.cat))]
    return [{ id: 'todos', label: 'Todos', count: list.length }, ...cats.map(c => ({ id: c, label: c, count: list.filter(a => a.cat === c).length }))]
}

export const filterByCategory = (list: Ambassador[], cat: string) => (cat === 'todos' ? list : list.filter(a => a.cat === cat))

// --- Fila do cliente ---

/** Sugestões enviadas ao cliente, na ordem de envio (e de criação). */
export function clientQueue(items: Prospect[]): Prospect[] {
    return items.filter(p => p.client?.sent).sort((a, b) => (a.client!.sentAt ?? '').localeCompare(b.client!.sentAt ?? '') || a.seq - b.seq)
}

export type ClientStatus = 'ok' | 'no' | 'pend'
export const clientStatusOf = (p: Prospect): ClientStatus => p.client?.dec?.st ?? 'pend'

export const CLIENT_STATUS: Record<ClientStatus, { label: string; tone: Tone; mark: string }> = {
    ok: { label: 'Faz sentido', tone: 'green', mark: '✓' },
    no: { label: 'Não faz sentido', tone: 'err', mark: '×' },
    pend: { label: 'Aguardando', tone: 'warn', mark: '•' },
}

export function clientCounts(items: Prospect[]) {
    const queue = clientQueue(items)
    const ok = queue.filter(p => clientStatusOf(p) === 'ok').length
    const no = queue.filter(p => clientStatusOf(p) === 'no').length
    return { total: queue.length, ok, no, pend: queue.length - ok - no, decided: ok + no }
}

/** Retorno do cliente numa linha: "Motivo — comentário"; '' sem decisão. */
export function clientFeedback(p: Prospect): string {
    const d = p.client?.dec
    if (!d) return ''
    return [d.motivo, d.com].filter(Boolean).join(' — ') || 'Sem comentário'
}

/** Próxima sugestão sem resposta depois de `from` (circular); -1 se todas respondidas. */
export function nextPending(queue: Prospect[], from: number): number {
    for (let k = 1; k <= queue.length; k++) {
        const j = (from + k) % queue.length
        if (clientStatusOf(queue[j]!) === 'pend') return j
    }
    return -1
}

export const firstPending = (queue: Prospect[]) => queue.findIndex(p => clientStatusOf(p) === 'pend')

// --- KPIs do topo (derivados, nada fixo) ---

export function kpisOf(ambassadors: Ambassador[], prospects: Prospect[]) {
    return {
        active: ambassadors.filter(a => a.status === 'ativo').length,
        toConfirm: ambassadors.filter(a => a.status === 'confirmar').length,
        waitingTerm: ambassadors.filter(a => a.status === 'termo').length,
        reach: ambassadors.reduce((t, a) => t + reachOf(a), 0),
        suggestions: prospects.length,
        undecided: prospects.filter(p => decisionOf(p) === 'undecided').length,
        ...clientCounts(prospects),
    }
}

/** Frase do cabeçalho (jeito calmo): quantos estão ativos e o que espera o cliente. */
export function headerSentence(k: Pick<ReturnType<typeof kpisOf>, 'active' | 'pend' | 'undecided'>): string {
    const head = `${k.active} ${k.active === 1 ? 'embaixador ativo' : 'embaixadores ativos'}`
    if (k.pend) return `${head}; ${k.pend} ${k.pend === 1 ? 'sugestão espera' : 'sugestões esperam'} o cliente.`
    if (k.undecided) return `${head}; ${k.undecided} ${k.undecided === 1 ? 'sugestão espera' : 'sugestões esperam'} a sua decisão.`
    return `${head}; nada esperando decisão.`
}

// --- Motor de busca ---

export const REDE_OPTIONS = [{ id: 'todas', label: 'Todas' }, { id: 'Instagram', label: 'Instagram' }, { id: 'TikTok', label: 'TikTok' }, { id: 'YouTube', label: 'YouTube' }] as const
export const ALC_OPTIONS = [{ id: 'todos', label: 'Todos' }, { id: 'medio', label: 'Até 1 mi' }, { id: 'macro', label: '1 a 10 mi' }, { id: 'mega', label: '10 mi +' }] as const
export type RedeFilter = (typeof REDE_OPTIONS)[number]['id']
export type AlcFilter = (typeof ALC_OPTIONS)[number]['id']

export const NICHO_EXAMPLES = ['Mães atípicas e inclusão', 'Professores criadores de conteúdo', 'Atletas com projetos sociais', 'Chefs e alimentação infantil', 'Pais influenciadores', 'Música infantil']

export function scanLabels(nicho: boolean): string[] {
    return nicho
        ? ['Interpretando o perfil descrito', 'Procurando criadores no nicho', 'Estimando audiência e redes', 'Checando afinidade com a causa da infância']
        : ['Lendo os perfis-base e seus temas', 'Cruzando audiências e seguidores em comum', 'Comparando tom de voz e valores', 'Checando afinidade com a causa da infância']
}

/** Tamanho pela maior rede: até 1 mi, 1 a 10 mi, 10 mi ou mais. */
export function tierOf(redes: SuggestionNetwork[]): Exclude<AlcFilter, 'todos'> {
    const m = Math.max(0, ...redes.map(r => r.n))
    return m < 1e6 ? 'medio' : m < 1e7 ? 'macro' : 'mega'
}

const byScore = (a: Prospect, b: Prospect) => (suggestionOf(b).score ?? 0) - (suggestionOf(a).score ?? 0) || a.seq - b.seq

/** "Semelhantes aos atuais": sugestões ligadas a algum perfil-base, filtradas por rede e tamanho, por afinidade. */
export function similarResults(items: Prospect[], bases: string[], rede: RedeFilter, alc: AlcFilter): Prospect[] {
    return items
        .filter(p => {
            const s = suggestionOf(p)
            return s.bases.some(b => bases.includes(b))
                && (rede === 'todas' || s.redes.some(r => r.r === rede))
                && (alc === 'todos' || tierOf(s.redes) === alc)
        })
        .sort(byScore)
}

/** Resultados da última busca por nicho, por afinidade. */
export const nichoResults = (items: Prospect[], ids: string[]) => items.filter(p => ids.includes(p.id)).sort(byScore)

/** "Nívea Soares e Fefê Schneider" / "Nicho: mães atípicas…". */
export function basesText(s: Pick<AmbassadorSuggestion, 'bases' | 'nicho'>, ambassadors: Pick<Ambassador, 'id' | 'name'>[]): string {
    if (s.nicho) return `Nicho: ${s.nicho}`
    const names = s.bases.map(b => ambassadors.find(a => a.id === b)?.name).filter((n): n is string => !!n)
    return names.length ? names.join(' e ') : '—'
}

export const BREAKDOWN_LABELS = ['Temas em comum', 'Perfil de público', 'Tom e valores', 'Afinidade com a infância']

/** Link público de uma rede da sugestão (Instagram, TikTok; o resto vai para a busca do YouTube, como o protótipo). */
export function networkUrl(r: SuggestionNetwork): string {
    const h = r.h.replace('@', '')
    if (r.r === 'Instagram') return `https://www.instagram.com/${encodeURIComponent(h)}/`
    if (r.r === 'TikTok') return `https://www.tiktok.com/@${encodeURIComponent(h)}`
    return `https://www.youtube.com/results?search_query=${encodeURIComponent(r.h)}`
}

// --- Régua: como abordar e impacto ---

export function howToOf(s: AmbassadorSuggestion, ambassadors: Pick<Ambassador, 'id' | 'name'>[]) {
    const top = topNetwork(s.redes) ?? { r: 'Instagram', h: s.handle, n: 0 }
    const bridge = s.bases.map(b => ambassadors.find(a => a.id === b)?.name).filter((n): n is string => !!n)
    return {
        canal: `DM no ${top.r} (${top.h || s.handle}) + e-mail comercial da bio`,
        ponte: bridge.length ? `Pedir apresentação a ${bridge.join(' ou ')}` : 'Sem ponte direta — via assessoria',
        gancho: s.why || '—',
        cuidado: s.atencao || '—',
    }
}

/** Referência do protótipo: R$ 80 por padrinho por mês. */
export const PADRINHO_MONTHLY = 80

export function impactText(padrinhos: number, first: string): string {
    if (!padrinhos) return 'Ainda sem padrinhos atribuídos. Use a LP com link rastreável para medir.'
    const kids = padrinhos === 1 ? 'criança apadrinhada' : 'crianças apadrinhadas'
    return `${num(padrinhos)} ${kids} por meio de ${first} · ≈ R$ ${num(padrinhos * PADRINHO_MONTHLY)}/mês em doações recorrentes (referência R$ ${PADRINHO_MONTHLY} por padrinho).`
}

// --- Foto ---

/** Recorte "cover" centralizado num quadrado `size`: onde desenhar a imagem no canvas. */
export function coverRect(width: number, height: number, size: number) {
    const k = Math.max(size / width, size / height)
    const w = width * k
    const h = height * k
    return { x: (size - w) / 2, y: (size - h) / 2, w, h }
}

/** Um passo para o embaixador ficar ativo (detalhe de Embaixadores atuais quando a base ainda não tem o perfil). */
export type OnboardingStep = { label: string; done: boolean; hint: string }

/** Os passos que a equipe acompanha: cadastro, termo de imagem, link e cupom, foto e o primeiro padrinho trazido. */
export function onboardingSteps(a: Pick<Ambassador, 'imageTermSignedAt' | 'link' | 'coupon' | 'padrinhos' | 'networks'>, hasPhoto: boolean): OnboardingStep[] {
    return [
        { label: 'Cadastro na Rede', done: true, hint: 'Na base de relacionamento.' },
        { label: 'Redes sociais', done: a.networks.some(n => n.n > 0), hint: a.networks.length ? 'Falta o número de seguidores.' : 'Nenhuma rede cadastrada.' },
        { label: 'Termo de uso de imagem', done: !!a.imageTermSignedAt, hint: a.imageTermSignedAt ? `Assinado em ${new Date(a.imageTermSignedAt).toLocaleDateString('pt-BR')}.` : 'Obrigatório antes de qualquer peça.' },
        { label: 'Link e cupom', done: !!(a.link && a.coupon), hint: a.link && a.coupon ? `Cupom ${a.coupon}.` : 'Saem ao cadastrar com termo assinado.' },
        { label: 'Foto do perfil', done: hasPhoto, hint: hasPhoto ? 'Enviada.' : 'Envie no quadro Foto ou busque com o agente.' },
        { label: 'Primeiro padrinho', done: (a.padrinhos ?? 0) > 0, hint: (a.padrinhos ?? 0) > 0 ? `${a.padrinhos} trazidos pelo link.` : 'Ainda nenhum pelo link ou cupom.' },
    ]
}
